import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withApplicationInsights } from '../../../components/telemetry';
import EmailTemplateDialog, { EmailTemplateFormValues } from '../../../components/coachingNavigator/emailTemplateDialog';
import ConfirmationPanel from '../../../components/confirmationPanel';
import { EmailTemplate } from '../../../models';
import config from '../../../config';
import { EmailTemplateService } from '../../../services/coachingNavigator/emailTemplateService';
import { Context } from '../../../components/context';
import { CoachingPageHeader } from '../../../components/coachingNavigator/coachingPageHeader';

const EmailTemplatesPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const appContext = useContext(Context);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<EmailTemplate | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

    const emailTemplateService = useMemo(() => new EmailTemplateService(config.api.baseUrl, '/emailTemplate'), []);

    useEffect(() => {
        let isActive = true;
        setLoading(true);

        const creatorEmail = (appContext.state.invoker?.email || '').trim();
        const load = async () => {
            try {
                if (!creatorEmail) {
                    if (isActive) setTemplates([]);
                    return;
                }
                const data = await emailTemplateService.getByCreator(creatorEmail);
                if (!isActive) return;
                const sorted = (data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setTemplates(sorted);
            } catch (error) {
                console.error('Failed to load email templates', error);
            } finally {
                if (isActive) setLoading(false);
            }
        };

        load();

        return () => {
            isActive = false;
        };
    }, [emailTemplateService, appContext.state.invoker]);

    const openCreate = useCallback(() => {
        setEditing(null);
        setDialogOpen(true);
    }, []);

    const openEdit = useCallback((template: EmailTemplate) => {
        setEditing(template);
        setDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setDialogOpen(false);
        setEditing(null);
    }, []);

    const handleSave = useCallback(
        async (values: EmailTemplateFormValues) => {
            const creatorEmail = (appContext.state.invoker?.email || '').trim();
            const payload: EmailTemplate = {
                id: editing?.id,
                name: values.name,
                subject: values.subject,
                body: values.body,
                creator: editing?.creator || creatorEmail || undefined,
            };

            try {
                const saved = await emailTemplateService.save(payload);
                setTemplates((prev) => {
                    const next = editing
                        ? prev.map((tpl) => (tpl.id === saved.id ? saved : tpl))
                        : [...prev, saved];
                    return next.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                });
                closeDialog();
            } catch (error) {
                console.error('Failed to save email template', error);
            }
        },
        [closeDialog, editing, emailTemplateService, appContext.state.invoker]
    );

    const handleDelete = useCallback(async () => {
        if (!deleteTarget?.id) return;
        try {
            await emailTemplateService.delete(deleteTarget.id);
            setTemplates((prev) => prev.filter((tpl) => tpl.id !== deleteTarget.id));
        } catch (error) {
            console.error('Failed to delete email template', error);
        }
        setDeleteTarget(null);
    }, [deleteTarget, emailTemplateService]);

    const templatesEmpty = !loading && templates.length === 0;

    return (
        <Box sx={{ pt: 2 }}>
            <CoachingPageHeader
                onBack={() => navigate('/management')}
                title={t('coachingNavigator.emailTemplates.title')}
                subtitle={t('coachingNavigator.emailTemplates.subtitle')}
                rightContent={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
                        {t('coachingNavigator.emailTemplates.add')}
                    </Button>
                }
                sx={{ mb: 1 }}
            />
            <Box sx={{ mt: 1 }}>
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        <Typography variant="body2">{t('coachingNavigator.emailTemplates.loading')}</Typography>
                    </Box>
                ) : templatesEmpty ? (
                    <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                        <Typography variant="body1">{t('coachingNavigator.emailTemplates.emptyTitle')}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {t('coachingNavigator.emailTemplates.emptySubtitle')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {templates.map((template) => (
                            <Card
                                key={template.id}
                                variant="outlined"
                                sx={{
                                    borderColor: 'divider',
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    boxShadow: 0,
                                    transition: 'all 0.25s ease-out',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: 4,
                                        borderColor: alpha(theme.palette.primary.main, 0.4),
                                        '.template-actions': { opacity: 1 },
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 2,
                                        '&:last-child': { pb: 2 },
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle1"
                                            noWrap
                                            sx={{
                                                fontWeight: 600,
                                                color: "text.primary"
                                            }}>
                                            {template.name}
                                        </Typography>
                                        <Typography variant="body2" noWrap sx={{
                                            color: "text.secondary"
                                        }}>
                                            {t('coachingNavigator.emailTemplates.form.subject')}: {template.subject}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{
                                                color: "text.secondary",
                                                mt: 0.5
                                            }}>
                                            {template.body}
                                        </Typography>
                                    </Box>
                                    <Box className="template-actions" sx={{ display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.25s ease-out' }}>
                                        <IconButton size="small" onClick={() => openEdit(template)}>
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(template)}>
                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </Box>
            <EmailTemplateDialog
                open={dialogOpen}
                onClose={closeDialog}
                onSubmit={handleSave}
                title={editing ? t('coachingNavigator.emailTemplates.dialog.editTitle') : t('coachingNavigator.emailTemplates.dialog.createTitle')}
                submitLabel={editing ? t('coachingNavigator.emailTemplates.dialog.save') : t('coachingNavigator.emailTemplates.dialog.create')}
                initialValues={editing ? { name: editing.name, subject: editing.subject, body: editing.body } : undefined}
            />
            <ConfirmationPanel
                showModal={!!deleteTarget}
                hideModal={() => setDeleteTarget(null)}
                confirmModal={handleDelete}
                action="delete"
                title={t('coachingNavigator.emailTemplates.delete.title')}
                content={t('coachingNavigator.emailTemplates.delete.body')}
            />
        </Box>
    );
};

export default withApplicationInsights(EmailTemplatesPage, 'EmailTemplatesPage');
